import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GradientTextAction, InputLabel, Poppins, Spacer, StyledAvatar, UserProfileModalSkeleton } from 'src/common';
import { Guard } from '../Guard';
import { useAuth } from 'src/state/auth';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getProfileCredentials } from 'src/utils/auth';
import { handleImageChange } from './util';
import MaterialInput from 'src/components/form/MaterialInput';
import Button from 'src/components/form/Button';
import styled from 'styled-components';
import { GenericSelect } from 'src/components/select/GenericSelect';
import { formatDate } from 'src/utils/misc';
import { Modal } from '../comps';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { AuditSection } from './sections';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import { useCache } from 'src/graphql/useCache';
import { Role, useUpdateUserMutation, useMemberQuery } from 'src/graphql/_generated';
import useInitialValues from './useInitialValues';
import { OrgAccess } from './OrgAccess';
import { SelectedOrgUnit } from './types';
import { RoleSelect } from 'src/components/select';
import { PBA_EXISTING_PERMISSIONS_REASON } from '../constants';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

const ModalWrap = styled(Modal)`
  width: 1350px;
  min-height: 200px;
  padding: 0 !important;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .p-head {
    display: flex;
    align-items: center;
    grid-gap: 10px;
  }

  .img-input {
    display: none;
  }

  form {
    .contents {
      display: grid;
      grid-gap: 20px;
      grid-template-columns: 1fr 1fr;
    }

    .btm-controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;

    &--left {
      text-align: left;
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    form {
      .contents {
        grid-gap: 20px;
      }
    }
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    form {
      .contents {
        grid-template-columns: 1fr;
      }
    }
  }
`;

const RoleHighlight = styled.div<{ $active: boolean }>`
  position: relative;
  border-radius: 6px;

  ${({ $active, theme }) =>
    $active &&
    `
    &::before {
      content: '';
      position: absolute;
      inset: -4px;
      border-radius: 8px;
      border: 2px solid ${theme.colors.gp1};
      animation: roleHighlightPulse 1.5s ease-in-out infinite;
    }

    @keyframes roleHighlightPulse {
      0%, 100% {
        opacity: 0.4;
        box-shadow: 0 0 8px ${theme.colors.gp1}40;
      }
      50% {
        opacity: 1;
        box-shadow: 0 0 16px ${theme.colors.gp1}80;
      }
    }
  `}
`;

const validationSchema = Yup.object({
  name: Yup.string().required('Required').max(100, 'Max 100 characters'),
});

interface UserModalProps {
  onClose: () => void;
  onUserUpdated?: () => void;
  userEmail?: string;
  workspaceId?: string;
  orgId?: string;
  highlightRoles?: boolean;
}

export const UserModal: React.FC<UserModalProps> = ({
  onClose,
  userEmail,
  workspaceId,
  orgId,
  onUserUpdated,
  highlightRoles,
}) => {
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<SelectedOrgUnit | undefined>(undefined);
  const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);
  const [err, setErr] = useState('');
  const { profile, isPlatformAdmin, isPartnerAdmin, flatOrganisations } = useAuth();
  const { refetchQuery } = useCache();

  const isOwnProfile = !userEmail || userEmail === profile?.user.email;
  const inputRef = useRef<HTMLInputElement>(null);

  const targetUserEmail = userEmail || profile?.user.email || null;
  const orgUnitId = workspaceId || orgId;

  // Org unit metadata from nav (already loaded in auth context)
  const orgUnitNav = orgUnitId ? flatOrganisations.find((o) => o.id === orgUnitId) : undefined;

  const memberQuerySkipped = !orgUnitId || !targetUserEmail;

  // Fast targeted query for the specific org unit's member data
  const {
    data: memberData,
    loading: memberLoading,
    error: memberError,
  } = useMemberQuery({
    variables: { orgUnitId: orgUnitId!, memberEmail: targetUserEmail! },
    skip: memberQuerySkipped,
    fetchPolicy: 'network-only',
  });

  // Derive userId from member query or own profile
  const userId = Number(memberData?.member?.user?.id || profile?.user.id);

  // Construct selectedOrgUnit from member query + nav metadata
  const initialOrgUnit = useMemo<SelectedOrgUnit | undefined>(() => {
    if (!orgUnitNav || !memberData?.member) return undefined;
    return {
      id: orgUnitNav.id,
      name: orgUnitNav.name,
      recordType: orgUnitNav.recordType,
      member: memberData.member,
    };
  }, [orgUnitNav, memberData]);

  // Set initial org unit from member query (once)
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (initialOrgUnit && !hasAutoSelectedRef.current) {
      setSelectedOrgUnit(initialOrgUnit);
      hasAutoSelectedRef.current = true;
    }
  }, [initialOrgUnit]);

  const unitNotFound = orgUnitId ? !orgUnitNav || (!memberLoading && !memberData?.member) || !!memberError : false;

  // For org-unit view: wait for member query (but don't wait if query was skipped)
  // For global/self view: no member query needed, just need profile
  const loading = orgUnitId
    ? !memberQuerySkipped && (memberLoading || (!memberData && !memberError)) && !hasAutoSelectedRef.current
    : !targetUserEmail;

  const { initError, initialValues } = useInitialValues({
    member: selectedOrgUnit?.member,
    isReady: !loading,
  });

  const [isSaving, setIsSaving] = useState(false);

  const [updateUser] = useUpdateUserMutation({
    onError: (err) => {
      setErr(err?.message || 'Something went wrong');
      setIsSaving(false);
    },
  });

  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    isValid,
    isSubmitting,
    dirty,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    validationSchema,
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setErr('');
      setIsSaving(true);

      if (!selectedOrgUnit) {
        await updateUser({
          variables: {
            userId,
            name: values.name,
            receiveNotifications: !!values.receiveNotifications,
          },
        });
        await refetchQuery({ include: ['profile'] });
      } else {
        await updateUser({
          variables: {
            orgUnitId: selectedOrgUnit.id,
            userId,
            name: values.name,
            role: values.role,
            receiveNotifications: !!values.receiveNotifications,
          },
        });
        await refetchQuery({ include: ['workspaceMembers', 'organisationMembers'] });
      }

      resetForm({ values });
      onUserUpdated?.();
      showSuccessToast(toastMessages.user.updated);
      setIsCloseRequested(true);
    },
  });

  const memberActions = selectedOrgUnit?.member?.memberActions;
  const canEditUser = isPlatformAdmin || memberActions?.canEditProfile;

  const getPermissionBasedDisabledOptions = (recordType: 'O' | 'W'): { value: Role; reason: string }[] => {
    const disabledOptions: { value: Role; reason: string }[] = [];
    const insufficientPermissionsReason = 'Insufficent permissions. Contact your admin for access.';

    if (recordType === 'O') {
      if (!memberActions?.canMakeAdmin) {
        disabledOptions.push({ value: Role.OrganisationAdmin, reason: insufficientPermissionsReason });
      }
      if (!memberActions?.canMakePartnerAdmin && values.role !== Role.PartnerBranchAdmin) {
        disabledOptions.push({
          value: Role.PartnerBranchAdmin,
          reason: isPlatformAdmin || isPartnerAdmin ? PBA_EXISTING_PERMISSIONS_REASON : insufficientPermissionsReason,
        });
      }
    } else if (recordType === 'W') {
      if (!memberActions?.canMakeAdmin) {
        disabledOptions.push({ value: Role.WorkspaceAdmin, reason: insufficientPermissionsReason });
      }
    }

    return disabledOptions;
  };

  const notifications = () => {
    const canEdit = isOwnProfile || canEditUser;
    return (
      <>
        <Spacer $px={20} />
        <InputLabel>DAILY DIGEST NOTIFICATIONS</InputLabel>
        <GenericSelect
          disabled={!canEdit}
          onChange={(value) => {
            const newValue = value.value === 'true';
            setFieldValue('receiveNotifications', newValue);
            mpEvent(MPEvents.NotificationToggle, { enabled: newValue });
          }}
          options={[
            { label: 'On', value: 'true' },
            { label: 'Off', value: 'false' },
          ]}
          value={values.receiveNotifications ? 'true' : 'false'}
        />
      </>
    );
  };

  const isWorkspace = selectedOrgUnit?.recordType === 'W';

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <ModalWrap onRequestClose={() => setIsCloseRequested(true)} isOpen center={false} persistent={isSaving}>
          <Spacer $px={40} />
          <div className="h-padding">
            {!loading ? (
              <>
                {unitNotFound ? (
                  <Poppins color="error" px={14}>
                    {memberError?.message || 'User not found'}
                  </Poppins>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="p-head">
                      <Poppins px={18} weight={600}>
                        {values?.defaultName?.toUpperCase()}
                      </Poppins>
                      <Poppins weight={500} px={14} color="cflowerBlue">
                        for
                      </Poppins>
                      <div css="flex:1;display: grid; max-width: 420px;margin-right: 30px;">
                        <OrgAccess
                          targetUserEmail={targetUserEmail}
                          isOwnProfile={isOwnProfile}
                          selectedOrgUnit={selectedOrgUnit}
                          onChange={(selected) => {
                            setSelectedOrgUnit(selected ?? undefined);
                          }}
                          closeModal={onClose}
                          targetUserName={values?.defaultName}
                        />
                      </div>
                    </div>
                    <Spacer $px={20} />
                    <StyledAvatar src={values.profilePhotoPath || selectedImage} $large>
                      {getProfileCredentials(initialValues?.name)}
                    </StyledAvatar>
                    <Spacer $px={13} />
                    <GradientTextAction onClick={() => alert('Not implemented')} px={14} weight={500}>
                      Update Photo
                    </GradientTextAction>
                    <input
                      className="img-input"
                      ref={inputRef}
                      type="file"
                      onChange={(e) => handleImageChange(e, setSelectedImage)}
                      accept="image/*"
                    />
                    <Spacer $px={10} />
                    <Poppins weight={500} px={14} color="cflowerBlue">
                      You are currently viewing {isOwnProfile ? 'your' : "user's"} profile details and activity
                      {selectedOrgUnit ? (
                        <>
                          {' '}
                          within{' '}
                          <Poppins px={14} weight={600}>
                            {selectedOrgUnit.name}
                            <Poppins weight={500} px={14} color="cflowerBlue">
                              {isWorkspace ? ' workspace' : ' organisation'}. To update{' '}
                              {isWorkspace ? 'organisation-level' : 'workspace-level'} details, select the{' '}
                              {isWorkspace ? 'organisation' : 'workspace'} from the dropdown above
                            </Poppins>
                          </Poppins>
                        </>
                      ) : (
                        ' across all your organisations and workspaces'
                      )}
                      .
                    </Poppins>

                    <Spacer $px={32} />
                    {initError ? (
                      <>
                        <Spacer $px={32} />
                        <div className="error error--left">{initError}</div>
                      </>
                    ) : (
                      <>
                        {selectedOrgUnit?.recordType === 'O' && (
                          <div className="contents">
                            <div>
                              <InputLabel>NAME</InputLabel>
                              <AutoGrowingInput
                                id="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                fullWidth
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                data-cy="name-input"
                                disabled={!canEditUser}
                              />
                              <Spacer $px={20} />
                              <InputLabel>ASSIGNED ROLE IN {selectedOrgUnit?.name.toUpperCase()}</InputLabel>
                              <RoleHighlight $active={!!highlightRoles}>
                                <RoleSelect
                                  disabled={!canEditUser}
                                  onChange={(value) => setFieldValue('role', value.value)}
                                  value={values.role}
                                  disabledOptions={[
                                    {
                                      value: Role.WorkspaceAdmin,
                                      reason: `This is a workspace-level role. Switch to a workspace using the dropdown above to assign this role to the user.`,
                                    },
                                    ...getPermissionBasedDisabledOptions('O'),
                                  ]}
                                  filteredOutRoles={[Role.CustomerManager]}
                                  optionTooltips={[
                                    {
                                      value: Role.NoAccess,
                                      reason:
                                        "When setting a users' permission to blocked the user will no longer be able to access the organisation",
                                    },
                                  ]}
                                />
                              </RoleHighlight>
                            </div>
                            <div>
                              <InputLabel>EMAIL ADDRESS</InputLabel>
                              <MaterialInput value={values?.email} fullWidth disabled />
                            </div>
                          </div>
                        )}
                        {selectedOrgUnit?.recordType === 'W' && (
                          <div className="contents">
                            <div>
                              <InputLabel>NAME</InputLabel>
                              <AutoGrowingInput
                                id="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                fullWidth
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                data-cy="name-input"
                                disabled={!canEditUser}
                              />
                              <Spacer $px={20} />
                              <InputLabel>ASSIGNED ROLE IN {selectedOrgUnit?.name.toUpperCase()}</InputLabel>
                              <RoleHighlight $active={!!highlightRoles}>
                                <RoleSelect
                                  disabled={!canEditUser}
                                  onChange={(value) => setFieldValue('role', value.value)}
                                  value={values.role}
                                  disabledOptions={[
                                    {
                                      value: Role.PartnerBranchAdmin,
                                      reason:
                                        'This is an organisation-level role. Switch to parent organisation using the dropdown above to assign this role to the user.',
                                    },
                                    {
                                      value: Role.OrganisationAdmin,
                                      reason: `This is an organisation-level role. Switch to parent organisation using the dropdown above to assign this role to the user.`,
                                    },
                                    ...getPermissionBasedDisabledOptions('W'),
                                  ]}
                                  filteredOutRoles={[Role.CustomerManager]}
                                  optionTooltips={[
                                    {
                                      value: Role.NoAccess,
                                      reason:
                                        "When setting a users' permission to blocked the user will no longer be able to access the workspace",
                                    },
                                  ]}
                                />
                              </RoleHighlight>
                              {notifications()}
                            </div>

                            <div>
                              <InputLabel>EMAIL ADDRESS</InputLabel>
                              <MaterialInput value={values?.email} fullWidth disabled />
                              <Spacer $px={20} />
                              <InputLabel>JOIN DATE</InputLabel>
                              <MaterialInput value={formatDate(values.acceptedDate)} fullWidth disabled />
                            </div>
                          </div>
                        )}
                        {!selectedOrgUnit && (
                          <div className="contents">
                            <div>
                              <InputLabel>NAME</InputLabel>
                              <AutoGrowingInput
                                id="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                fullWidth
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                data-cy="name-input"
                              />
                              {notifications()}
                            </div>
                            <div>
                              <InputLabel>EMAIL ADDRESS</InputLabel>
                              <MaterialInput value={values?.email} fullWidth disabled />
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {err && (
                      <>
                        <Spacer $px={20} />
                        <div className="error error--left">{err}</div>
                      </>
                    )}
                    <Spacer $px={50} />
                    <div className="btm-controls">
                      <Button
                        primary
                        type="submit"
                        disabled={!isValid || isSaving || !dirty}
                        loading={isSaving}
                        data-cy="update-button"
                        $constWidth={220}
                        onClick={() =>
                          mpEvent(MPEvents.ButtonClick, {
                            button: 'Update',
                            value: userId,
                            tags: ['USER'],
                          })
                        }
                      >
                        UPDATE
                      </Button>
                    </div>
                    {!!selectedOrgUnit?.recordType || isOwnProfile ? (
                      <AuditSection userId={userId} selectedOrgUnit={selectedOrgUnit} />
                    ) : (
                      <Spacer $px={20} />
                    )}
                  </form>
                )}
              </>
            ) : (
              <>
                <UserProfileModalSkeleton />
                <Spacer $px={20} />
              </>
            )}
          </div>
        </ModalWrap>
      }
    />
  );
};
